import React, { useState, useEffect, useCallback } from 'react';
import QRCode from 'qrcode';
import { Download, Eye, EyeOff, Loader, QrCode } from 'lucide-react';
import { QRCodeData, QRHistoryItem } from '../types/qr-types';
import QRInputForm from './QRInputForm';
import { t } from '../i18n/translations';

interface QRGeneratorProps {
  qrData: QRCodeData;
  onDataChange: (data: QRHistoryItem) => void;
  onSave: (data: QRHistoryItem) => void;
  language: 'en' | 'zh';
}

const QRGenerator: React.FC<QRGeneratorProps> = ({ qrData, onDataChange, onSave, language }) => {
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [showPreview, setShowPreview] = useState(true);

  function validateFacebookUrl(url: string, type: 'profile' | 'share'): boolean {
    if (type === 'profile') {
      return /^https?:\/\/(www\.)?facebook\.com\/[a-zA-Z0-9._-]+$/.test(url);
    }
    return /^https?:\/\//.test(url);
  }

  function validateUrl(url: string): boolean {
    try {
      new URL(url);
      return /^https?:\/\//.test(url);
    } catch {
      return false;
    }
  }

  function validateEmail(email: string): boolean {
    return /^[\w.-]+@[\w.-]+\.[a-zA-Z]{2,}$/.test(email);
  }

  function validatePhone(phone: string): boolean {
    // 简单国际/中国手机号校验，可根据需求调整
    return /^\+?\d{7,15}$/.test(phone);
  }

  function validateTwitterProfile(input: string): boolean {
    if (!input) return false;
    if (/^@\w{1,15}$/.test(input)) return true;
    if (/^https?:\/\/(www\.)?(twitter\.com|x\.com)\/\w{1,15}$/.test(input)) return true;
    return false;
  }

  function validateTweetText(text: string): boolean {
    return !!text && text.length <= 280;
  }

  const getQRCodeContent = useCallback((qrData: QRCodeData): string => {
    switch (qrData.type) {
      case 'url':
        if (!validateUrl(qrData.url ?? '')) {
          return '';
        }
        return qrData.url ?? '';
      case 'text':
        return qrData.text ?? '';
      case 'email': {
        if (!validateEmail(qrData.email ?? '')) {
          return '';
        }
        let mailto = `mailto:${qrData.email}`;
        const params: string[] = [];
        if (qrData.subject) params.push(`subject=${encodeURIComponent(qrData.subject)}`);
        if (qrData.body) params.push(`body=${encodeURIComponent(qrData.body)}`);
        if (params.length) mailto += `?${params.join('&')}`;
        return mailto;
      }
      case 'phone':
        if (!validatePhone(qrData.phone ?? '')) {
          return '';
        }
        return `tel:${qrData.phone}`;
      case 'sms': {
        if (!validatePhone(qrData.phone ?? '')) {
          return '';
        }
        let sms = `smsto:${qrData.phone}`;
        if (qrData.message) sms += `:${encodeURIComponent(qrData.message)}`;
        return sms;
      }
      case 'wifi': {
        if (!qrData.ssid) return '';
        return `WIFI:T:${qrData.encryption};S:${qrData.ssid};P:${qrData.password};H:${qrData.hidden ? 'true' : 'false'};;`;
      }
      case 'facebook':
        if (!validateFacebookUrl(qrData.url ?? '', qrData.facebookType)) {
          return '';
        }
        return qrData.url ?? '';
      case 'twitter': {
        if (qrData.twitterType === 'profile') {
          if (!validateTwitterProfile(qrData.username ?? '')) {
            return '';
          }
          // 支持@username或完整链接
          let username = qrData.username?.trim() ?? '';
          if (username.startsWith('@')) username = username.slice(1);
          if (username.startsWith('http')) {
            // 直接用完整链接
            return username;
          }
          return `https://twitter.com/${username}`;
        } else {
          if (!validateTweetText(qrData.tweetText ?? '')) {
            return '';
          }
          return `https://twitter.com/intent/tweet?text=${encodeURIComponent(qrData.tweetText ?? '')}`;
        }
      }
      default:
        return '';
    }
  }, []);

  const generateQRCode = useCallback(async () => {
    const content = getQRCodeContent(qrData);
    if (!content.trim()) {
      setQrCodeUrl('');
      return;
    }

    setIsGenerating(true);
    try {
      const options = {
        width: qrData.size,
        color: {
          dark: qrData.color,
          light: qrData.backgroundColor,
        },
        errorCorrectionLevel: qrData.errorLevel,
        margin: 0,
      };

      const dataUrl = await QRCode.toDataURL(content, options);
      setQrCodeUrl(dataUrl);
    } catch (error) {
      console.error('Error generating QR code:', error);
    } finally {
      setIsGenerating(false);
    }
  }, [qrData, getQRCodeContent]);

  useEffect(() => {
    generateQRCode();
  }, [generateQRCode]);

  const downloadQR = async (format: 'png' | 'svg' = 'png') => {
    const content = getQRCodeContent(qrData);
    if (!content.trim()) return;

    try {
      const options = {
        width: qrData.size,
        color: {
          dark: qrData.color,
          light: qrData.backgroundColor,
        },
        errorCorrectionLevel: qrData.errorLevel,
        margin: 0,
      };

      let dataUrl: string;
      let filename: string;

      if (format === 'svg') {
        const svgString = await QRCode.toString(content, { ...options, type: 'svg' });
        // 自动提取二维码内容实际大小（如viewBox或path最大坐标）
        // 先尝试从svgString中提取viewBox或width属性
        let unitSize = 21; // 默认21，适配大部分二维码
        const viewBoxMatch = svgString.match(/viewBox="0 0 (\d+) (\d+)"/);
        if (viewBoxMatch) {
          unitSize = parseInt(viewBoxMatch[1], 10);
        } else {
          const widthMatch = svgString.match(/width="(\d+)"/);
          if (widthMatch) {
            unitSize = parseInt(widthMatch[1], 10);
          }
        }
        const scale = qrData.size / unitSize;
        // 移除原有width/height/viewBox属性
        let modifiedSvgString = svgString.replace(/(width|height|viewBox)="[^"]*"/g, '');
        // 用<g transform="scale(...)"/>包裹内容
        modifiedSvgString = modifiedSvgString.replace(
          /(<svg[^>]*>)([\s\S]*)(<\/svg>)/,
          `$1<g transform="scale(${scale})">$2</g>$3`
        );
        // 添加自定义viewBox
        modifiedSvgString = modifiedSvgString.replace('<svg', `<svg viewBox="0 0 ${qrData.size} ${qrData.size}"`);
        const blob = new Blob([modifiedSvgString], { type: 'image/svg+xml' });
        dataUrl = URL.createObjectURL(blob);
        filename = `qrcode-${Date.now()}.svg`;
      } else {
        dataUrl = await QRCode.toDataURL(content, options);
        filename = `qrcode-${Date.now()}.png`;
      }

      const link = document.createElement('a');
      link.download = filename;
      link.href = dataUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      if (format === 'svg') {
        URL.revokeObjectURL(dataUrl);
      }

      // Save to history
      const historyItem: QRHistoryItem = {
        ...qrData,
        dataUrl: qrCodeUrl,
        timestamp: Date.now(),
        content
      };
      onSave(historyItem);
    } catch (error) {
      console.error('Error downloading QR code:', error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Input Form */}
      <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
        <h2 className="text-xl font-semibold text-white mb-4">{t('enterContent', language)}</h2>
        <QRInputForm
          qrData={qrData}
          onDataChange={onDataChange}
          language={language}
        />
      </div>

      {/* QR Code Display */}
      <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-white">{t('yourQrCode', language)}</h3>
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            {showPreview ? (
              <EyeOff className="w-5 h-5 text-white" />
            ) : (
              <Eye className="w-5 h-5 text-white" />
            )}
          </button>
        </div>

        {showPreview && (
          <div className="text-center">
            {isGenerating ? (
              <div className="flex items-center justify-center py-12">
                <Loader className="w-8 h-8 animate-spin text-white" />
              </div>
            ) : qrCodeUrl ? (
              <div className="space-y-6">
                <div className="inline-block p-4 bg-white rounded-xl shadow-lg">
                  <img
                    src={qrCodeUrl}
                    alt="Generated QR Code"
                    className="max-w-full h-auto"
                    style={{ width: qrData.size, height: qrData.size }}
                  />
                </div>
                
                {/* Download Buttons */}
                <div className="flex flex-wrap gap-3 justify-center">
                  <button
                    onClick={() => downloadQR('png')}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    PNG
                  </button>
                  <button
                    onClick={() => downloadQR('svg')}
                    className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    SVG
                  </button>
                </div>
              </div>
            ) : (
              <div className="py-12 text-gray-400">
                <QrCode className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p>{t('enterContentAbove', language)}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default QRGenerator;